// Story 1.30 — TanStack Query mutation: PATCH /api/v1/admin/bulletin/posts/:id.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { BulletinPostListItem } from "./useAdminBulletinList";

export type UpdateBulletinInput = {
  id: string;
  title?: string;
  slug?: string;
  body_md?: string;
};

export type UpdateBulletinResponse = {
  data: BulletinPostListItem;
};

async function updatePost(
  input: UpdateBulletinInput
): Promise<UpdateBulletinResponse> {
  const { id, ...patch } = input;
  const idempotencyKey = crypto.randomUUID();
  const response = await fetch(`/api/v1/admin/bulletin/posts/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(patch),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Update bulletin failed (HTTP ${response.status})`;
    let code: string | undefined;
    let slug: string | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
      code = raw?.error?.code;
      slug = raw?.error?.slug;
    } catch {
      // body not JSON
    }
    const err = new Error(message) as Error & { code?: string; slug?: string };
    err.code = code;
    err.slug = slug;
    throw err;
  }
  return (await response.json()) as UpdateBulletinResponse;
}

export function useUpdateBulletinPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updatePost,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin-bulletin-list"] });
      qc.invalidateQueries({ queryKey: ["admin-bulletin-detail", vars.id] });
    },
  });
}
